/**
 * Client-side photo normalisation for the admin uploader (OH-115).
 *
 * The upload path is a presigned PUT: the browser sends the bytes straight to
 * R2 and the API never sees them. That leaves the browser as the only place a
 * ceiling can be applied on the way in — and it has to happen BEFORE the
 * upload URL is requested, because the presigned URL pins Content-Length into
 * its signature (see internal/media/r2/presigner.go). Uploading a resized body
 * against a URL signed for the original size is rejected outright.
 *
 * Why this matters: the bucket filled up with phone originals up to 6341px and
 * ~10 MB each, while the guest site never renders wider than ~1920.
 */

/** Longest side we keep, in px. 2560 leaves headroom over the 1920 the site renders. */
export const MAX_SIDE = 2560;

/** JPEG quality for re-encoded photos. Matches imaging.DefaultJPEGQuality on the server. */
export const JPEG_QUALITY = 0.85;

export interface NormalizedImage {
  /** The bytes to upload — the original File when no work was needed. */
  blob: Blob;
  /** MIME type of `blob`; may differ from the input only in that it is normalised. */
  type: string;
  /** Dimensions of `blob` as a browser will render them. */
  width: number;
  height: number;
  /** True when the image was re-encoded rather than passed through. */
  changed: boolean;
}

/**
 * Scale `file` down so neither side exceeds `maxSide`, re-encoding in the same
 * format. Images already within the ceiling are returned untouched, so we do
 * not spend a JPEG generation on them.
 *
 * `imageOrientation: 'from-image'` is load-bearing: without it the canvas draws
 * raw pixels and drops the EXIF rotation tag, which silently lands portrait
 * phone photos on their side. Everything here is phone photos.
 *
 * If the browser cannot decode the file (or the platform lacks
 * createImageBitmap), the original File is returned unchanged — an upload that
 * works beats a resize that throws.
 */
export async function normalizeImage(
  file: File,
  maxSide: number = MAX_SIDE,
  quality: number = JPEG_QUALITY,
): Promise<NormalizedImage> {
  const fallback = (): NormalizedImage => ({
    blob: file,
    type: file.type,
    width: 0,
    height: 0,
    changed: false,
  });

  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') {
    return fallback();
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return fallback();
  }

  try {
    const { width: srcW, height: srcH } = bitmap;
    if (!srcW || !srcH) return fallback();

    const longest = Math.max(srcW, srcH);
    if (longest <= maxSide) {
      // Already small enough: keep the original bytes, but report the real
      // dimensions so the row stops being "unmeasured".
      return { blob: file, type: file.type, width: srcW, height: srcH, changed: false };
    }

    const scale = maxSide / longest;
    const width = Math.max(1, Math.round(srcW * scale));
    const height = Math.max(1, Math.round(srcH * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return fallback();
    ctx.drawImage(bitmap, 0, 0, width, height);

    // Keep the source format: the R2 key's extension is derived from the mime
    // the server was told about, so switching formats here would leave a .jpg
    // key holding PNG bytes.
    const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, type, type === 'image/jpeg' ? quality : undefined);
    });
    if (!blob) return fallback();

    return { blob, type, width, height, changed: true };
  } finally {
    bitmap.close?.();
  }
}
